import { useTranslation } from 'react-i18next';

import { REQUEST_ESSENCE_MAX_LENGTH } from '@crm/entities/deal';
import { Separator } from '@crm/shared/ui';
import { DealDetailsBlock } from '@crm/widgets/deal-details-content/ui/DealDetailsBlock/DealDetailsBlock';

import { EditableTextarea } from './EditableTextarea';
import { InformationFieldError } from './InformationFieldError';
import { InfoSection } from './InfoSection';
import { formatDateValue, formatSumDisplayValue } from '../../model/informationBlockFields';
import { useInformationBlockEditing } from '../../model/useInformationBlockEditing';

import type { InfoItem } from '../../model/informationBlockFields';
import type { TDealDetails } from '@crm/entities/deal';

interface InformationBlockProps {
    dealDetailsData: TDealDetails;
}

export const InformationBlock = ({ dealDetailsData }: InformationBlockProps) => {
    const { t } = useTranslation();
    const { dealData } = dealDetailsData;
    const {
        fieldErrors,
        handleDateFieldCommit,
        handleFieldEdit,
        handleFieldBlur,
        handleFieldFocus,
        handleFieldInput,
        handleFieldKeyDown,
    } = useInformationBlockEditing(dealData);
    const requestEssenceError = fieldErrors.request_essence;
    const requestEssenceErrorId = 'request_essence-error';

    const clientInfo: InfoItem[] = [
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.customerName.label'),
            value: dealData?.customerName,
            fieldName: 'customer_name',
            placeholder: t(
                'crm.dealDetailsContent.informationBlock.fields.customerName.placeholder'
            ),
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.phone.label'),
            value: dealData?.phone,
            fieldName: 'phone',
            placeholder: '79991234567',
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.email.label'),
            value: dealData?.email,
            fieldName: 'email',
            placeholder: 'smetter@test.ru',
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.source.label'),
            value: dealData?.source,
            fieldName: 'source',
            placeholder: t('crm.dealDetailsContent.informationBlock.fields.source.placeholder'),
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.receivedAt.label'),
            value: formatDateValue(dealData?.receivedAt),
            fieldName: 'received_at',
            placeholder: t('crm.dealDetailsContent.informationBlock.fields.date.placeholder'),
        },
    ];
    const objectInfo: InfoItem[] = [
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.objectType.label'),
            value: dealData?.objectType,
            fieldName: 'object_type',
            placeholder: t('crm.dealDetailsContent.informationBlock.fields.objectType.placeholder'),
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.area.label', {
                unit: dealData?.unit,
            }),
            rawValue: dealData?.area,
            value: formatSumDisplayValue(dealData?.area),
            fieldName: 'area',
            placeholder: '50',
        },
        {
            label: t('crm.dealDetailsContent.informationBlock.fields.approximateBudget.label', {
                currency: dealData?.currency.sign,
            }),
            rawValue: dealData?.approximateBudget,
            value: formatSumDisplayValue(dealData?.approximateBudget),
            fieldName: 'approximate_budget',
            placeholder: '200 000',
        },
    ];
    // const dealInfo: InfoItem[] = [
    //     {
    //         label: t('crm.dealDetailsContent.informationBlock.fields.customerStage.label'),
    //         value: dealData?.customerStage,
    //         fieldName: 'customer_stage',
    //         placeholder: t('crm.dealDetailsContent.informationBlock.fields.value.placeholder'),
    //     },
    //     {
    //         label: t('crm.dealDetailsContent.informationBlock.fields.urgency.label'),
    //         value: dealData?.urgency,
    //         fieldName: 'urgency',
    //         placeholder: t('crm.dealDetailsContent.informationBlock.fields.urgency.placeholder'),
    //     },
    //     {
    //         label: t('crm.dealDetailsContent.informationBlock.fields.expectedClosingAt.label'),
    //         value: formatDateValue(dealData?.expectedClosingAt),
    //         fieldName: 'expected_closing_at',
    //         placeholder: t('crm.dealDetailsContent.informationBlock.fields.date.placeholder'),
    //     },
    //     ...(dealData?.context?.reason
    //         ? [
    //               {
    //                   label: t('crm.dealDetailsContent.informationBlock.fields.lostReason.label'),
    //                   value: dealData.context.reason,
    //                   fieldName: 'deal_details',
    //                   placeholder: t(
    //                       'crm.dealDetailsContent.informationBlock.fields.value.placeholder'
    //                   ),
    //               } satisfies InfoItem,
    //           ]
    //         : []),
    // ];

    return (
        <DealDetailsBlock title={t('crm.dealDetailsContent.informationBlock.title')}>
            <div className="flex flex-col gap-2">
                <InfoSection
                    items={clientInfo}
                    fieldErrors={fieldErrors}
                    onFieldBlur={handleFieldBlur}
                    onFieldKeyDown={handleFieldKeyDown}
                    onFieldFocus={handleFieldFocus}
                    onFieldInput={handleFieldInput}
                    onFieldEdit={handleFieldEdit}
                    onDateFieldCommit={handleDateFieldCommit}
                />
                <Separator />
                <InfoSection
                    items={objectInfo}
                    fieldErrors={fieldErrors}
                    onFieldBlur={handleFieldBlur}
                    onFieldKeyDown={handleFieldKeyDown}
                    onFieldFocus={handleFieldFocus}
                    onFieldInput={handleFieldInput}
                    onFieldEdit={handleFieldEdit}
                    onDateFieldCommit={handleDateFieldCommit}
                />
                {/* <Separator /> */}
                {/* <InfoSection */}
                {/*    items={dealInfo} */}
                {/*    fieldErrors={fieldErrors} */}
                {/*    onFieldBlur={handleFieldBlur} */}
                {/*    onFieldKeyDown={handleFieldKeyDown} */}
                {/*    onFieldFocus={handleFieldFocus} */}
                {/*    onFieldInput={handleFieldInput} */}
                {/*    onFieldEdit={handleFieldEdit} */}
                {/*    onDateFieldCommit={handleDateFieldCommit} */}
                {/* /> */}
                <Separator />
                <div className="flex w-full flex-col gap-3">
                    <h3 className="text-xs leading-5 text-smtr-main-tertiary-100">
                        {t('crm.dealDetailsContent.informationBlock.fields.requestEssence.label')}
                    </h3>
                    <div className="group/info-field relative">
                        <EditableTextarea
                            value={dealData?.requestEssence}
                            placeholder={t(
                                'crm.dealDetailsContent.informationBlock.fields.requestEssence.placeholder'
                            )}
                            aria-label={t(
                                'crm.dealDetailsContent.informationBlock.fields.requestEssence.label'
                            )}
                            aria-invalid={Boolean(requestEssenceError)}
                            aria-describedby={
                                requestEssenceError ? requestEssenceErrorId : undefined
                            }
                            name="request_essence"
                            maxLength={REQUEST_ESSENCE_MAX_LENGTH}
                            className={requestEssenceError ? 'pr-7' : undefined}
                            onInput={(event) => handleFieldInput('request_essence', event)}
                            onBlur={(event) =>
                                handleFieldBlur('request_essence', dealData?.requestEssence, event)
                            }
                            onKeyDown={(event) => {
                                if (event.key !== 'Enter') {
                                    handleFieldKeyDown(
                                        'request_essence',
                                        dealData?.requestEssence,
                                        event
                                    );
                                }
                            }}
                        />
                        {requestEssenceError && (
                            <InformationFieldError
                                id={requestEssenceErrorId}
                                errorMessage={requestEssenceError}
                            />
                        )}
                    </div>
                </div>
            </div>
        </DealDetailsBlock>
    );
};
